import { TailSpin } from 'react-loader-spinner'
import { Blue300, Red800 } from '../../lib/colors'
import ProfileSelectorModal, {
  ProfileSelectorModalType,
} from '../../components/ProfileSelectorModal'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAtomValue } from 'jotai'
import { IoMdCloseCircleOutline } from 'react-icons/io'
import {
  availableTestsMostPopularSubscriptionAtom,
  clientFavouriteTestsSubscriptionAtom,
  profilesSubscriptionAtom,
} from '../../store/subscription.atoms'
import { BasketContext, isTestBasketItem } from '../../provider/basket'
import { AuthContext } from '../../provider/auth'
import { useI18n } from '../../provider/i18n'
import { SearchTestFragment } from '../../generated/urql.client'
import { TestItem } from '../../components/TestItem'

const Tests = () => {
  const [isInited, setIsInited] = useState(false)
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const i18n = useI18n()
  const [{ data: mostPopularTestsData, fetching: mostPopularTestsFetching }] =
    useAtomValue(availableTestsMostPopularSubscriptionAtom)
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const [
    { data: clientFavouriteTestsData, fetching: clientFavouriteTestsFetching },
  ] = useAtomValue(clientFavouriteTestsSubscriptionAtom)
  const profileSelectorModalRef = useRef<ProfileSelectorModalType>(null)

  const mostPopularTests = mostPopularTestsData?.search_test_most_popular || []
  const clientFavouriteTests =
    clientFavouriteTestsData?.client_favourite_test.map(({ test }) => test) ||
    []

  const isFetching = useMemo(
    () =>
      (mostPopularTestsFetching || clientFavouriteTestsFetching) && !isInited,
    [mostPopularTestsFetching, clientFavouriteTestsFetching, isInited]
  )

  useEffect(() => {
    if (!isFetching) {
      setIsInited(true)
    }
  }, [isFetching])

  const onTestAddClick = useCallback(
    (test: SearchTestFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!auth.isLogged || profilesCount === 0) {
        basket.addItem({ test })
      } else if (profilesCount === 1) {
        basket.addItem({ test, profile: profilesData?.client_profile[0] })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ test, profile })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isTestBasketItem(item) &&
                item.test.id === test.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [auth.isLogged, basket, profilesData?.client_profile]
  )

  if (isFetching) {
    return (
      <TailSpin
        color={Blue300}
        height={32}
        width={32}
        wrapperClass="flex justify-center p-8"
      />
    )
  }

  return (
    <div className="mb-16">
      {basket.address?.location && mostPopularTests.length > 0 && (
        <>
          <div className="text-blue-700 my-5 flex items-center gap-1">
            {i18n.t('screens.catalog.index.mostPopular.header')}
          </div>
          <ul className="md:mx-5 flex flex-col gap-5">
            {mostPopularTests.map((test) => (
              <li key={test.id}>
                <TestItem
                  test={test}
                  variant="green"
                  onTestAddClick={() => onTestAddClick(test)}
                  showAddButton={
                    auth.isLogged
                      ? basket.items
                          .filter(isTestBasketItem)
                          .filter(({ test: { id } }) => test.id === id).length <
                        (profilesData?.client_profile.length || 0)
                      : !basket.items.find(
                          (item) =>
                            isTestBasketItem(item) && test.id === item.test.id
                        )
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {clientFavouriteTests.length > 0 && (
        <>
          <div className="text-blue-700 my-5 flex items-center gap-1">
            {i18n.t('screens.catalog.index.favourite.header')}
            {clientFavouriteTestsFetching && (
              <TailSpin color={Blue300} height={16} width={16} />
            )}
          </div>
          <ul className="md:mx-5 flex flex-col gap-5">
            {clientFavouriteTests.map((test) => (
              <li key={test.id}>
                <TestItem
                  test={test}
                  onTestAddClick={() => onTestAddClick(test)}
                  showAddButton={
                    auth.isLogged
                      ? basket.items
                          .filter(isTestBasketItem)
                          .filter(({ test: { id } }) => test.id === id).length <
                        (profilesData?.client_profile.length || 0)
                      : !basket.items.find(
                          (item) =>
                            isTestBasketItem(item) && test.id === item.test.id
                        )
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {mostPopularTests.length === 0 && clientFavouriteTests.length === 0 && (
        <div className="text-blue-700 mb-5 flex flex-col items-center gap-1 p-2 justify-center text-center">
          <IoMdCloseCircleOutline size={48} color={Red800} />
          <p>{i18n.t('screens.catalog.tests.empty')}</p>
        </div>
      )}

      <ProfileSelectorModal ref={profileSelectorModalRef} />
    </div>
  )
}

export default Tests
